import { JobState } from '@treadinc/horizon-api-spec';

import {
  DEFAULT_INCLUDED_JOB_STATES,
  EXCLUDED_JOB_STATES,
  FILES_QUERY_CONTEXT_PREFIX,
  JOBS_QUERY_CONTEXT_PREFIX,
} from './constants';

const BILLING_LINES_QUERY_KEY_SUFFIX = '_billingLines';

export function normalizeJobStateSelection(states?: JobState[]) {
  const selectableStates = (states ?? []).filter(
    (state) => !EXCLUDED_JOB_STATES.includes(state),
  );

  return selectableStates.length ? selectableStates : undefined;
}

export function getEffectiveJobStates(jobStates?: JobState[]) {
  return jobStates?.length ? jobStates : DEFAULT_INCLUDED_JOB_STATES;
}

export function omitWhenEmpty<T>(values?: T[]) {
  return values?.length ? values : undefined;
}

export function makeFilesQueryContextKey() {
  return `${FILES_QUERY_CONTEXT_PREFIX}_files`;
}

export function makeJobsQueryContextKey(orderId: string) {
  return `${JOBS_QUERY_CONTEXT_PREFIX}_${orderId}_jobs`;
}

export function makeLoadsQueryContextBaseKey(jobId: string) {
  return `${JOBS_QUERY_CONTEXT_PREFIX}_${jobId}`;
}

export function makeLoadsQueryContextKey(
  jobId: string,
  target: 'loads' | 'load_cycles' | 'tickets',
) {
  if (target === 'loads') {
    return `${makeLoadsQueryContextBaseKey(jobId)}_loads`;
  }

  if (target === 'load_cycles') {
    return `${makeLoadsQueryContextBaseKey(jobId)}_loadCycles`;
  }

  return `${makeLoadsQueryContextBaseKey(jobId)}_tickets`;
}

export function makeJobsBillingLinesQueryKey(orderId: string) {
  return `${JOBS_QUERY_CONTEXT_PREFIX}_${orderId}${BILLING_LINES_QUERY_KEY_SUFFIX}`;
}

export function isJobsBillingLinesQueryKey(queryKey: readonly unknown[]) {
  return queryKey.some((key) => {
    const keyAsString = String(key);

    return (
      keyAsString.startsWith(JOBS_QUERY_CONTEXT_PREFIX) &&
      keyAsString.endsWith(BILLING_LINES_QUERY_KEY_SUFFIX)
    );
  });
}

export function isJobsBillingLinesQueryKeyOfAnyJob(
  queryKey: readonly unknown[],
  jobIds: ReadonlySet<string>,
) {
  return (
    isJobsBillingLinesQueryKey(queryKey) &&
    queryKey.some(
      (keyPart) =>
        Array.isArray(keyPart) &&
        keyPart.some((jobId: unknown) => typeof jobId === 'string' && jobIds.has(jobId)),
    )
  );
}

export function makeAllFilesQueryContextKey(jobId: string, companyId: string) {
  return `${JOBS_QUERY_CONTEXT_PREFIX}_${jobId}_${companyId}_allFiles`;
}
